import { Box, Typography, Chip, Collapse } from "@mui/material";
import { FitnessCenter, CheckCircle, Assessment, ExpandMore, ExpandLess } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { getWorkout } from "../../../services/PlanSerice";
import { targetMuscles } from "../../../enums/TargetMuscles";

const MobileWeekSummary = ({ planState, reloadKey }) => {
  const [stats, setStats] = useState({
    totalSets: 0,
    totalExercises: 0,
    completedWorkouts: 0,
    totalWorkouts: 0
  });
  const [muscleBreakdown, setMuscleBreakdown] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Define muscle groupings
  const muscleGroupings = {
    'LEGS_QUADS': { group: 'LEGS_QUADS', name: 'רגליים-קדמית', order: 1 },
    'LEGS_HAMSTRINGS': { group: 'LEGS_HAMSTRINGS', name: 'רגליים-אחורית', order: 2 },
    'LEGS_GLUTES': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
    'LEGS_ADDUCTORS': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
    'LEGS_ABDUCTORS': { group: 'LEGS_GLUTES', name: 'ישבן', order: 3 },
    'LEGS_CALVES': { group: 'LEGS_CALVES', name: 'תאומים', order: 4 },
    'FEET': { group: 'FEET', name: 'כפות רגליים', order: 4.5 },
    'BACK_ROWES': { group: 'BACK', name: 'גב', order: 5 },
    'BACK_PULLS': { group: 'BACK', name: 'גב', order: 5 },
    'BACK_LOWER_BACK': { group: 'BACK', name: 'גב', order: 5 },
    'CHEST_UPPER': { group: 'CHEST', name: 'חזה', order: 6 },
    'CHEST_MIDDLE': { group: 'CHEST', name: 'חזה', order: 6 },
    'CHEST_LOWER': { group: 'CHEST', name: 'חזה', order: 6 },
    'ARMS_BICEPS': { group: 'ARMS_BICEPS', name: 'יד קדמית', order: 7 },
    'ARMS_TRICEPS': { group: 'ARMS_TRICEPS', name: 'יד אחורית', order: 8 },
    'ARMS_FOREARMS': { group: 'ARMS_FOREARMS', name: 'אמות', order: 9 },
    'SHOULDERS_FRONT_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
    'SHOULDERS_LATERAL_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
    'SHOULDERS_REAR_DELTS': { group: 'SHOULDERS', name: 'כתפיים', order: 10 },
    'SHOULDERS_TRAPS': { group: 'SHOULDERS_TRAPS', name: 'טרפזים', order: 11 },
    'NECK': { group: 'NECK', name: 'צוואר', order: 11.5 },
    'ABS_OBLIQUES': { group: 'ABS', name: 'בטן', order: 12 },
    'ABS': { group: 'ABS', name: 'בטן', order: 12 },
    'CARDIO': { group: 'CARDIO', name: 'קרדיו', order: 13 },
  };

  useEffect(() => {
    const fetchAllWorkouts = async () => {
      if (!planState?.plan?.blocks) {
        setLoading(false);
        return;
      }

      const blocks = planState.plan.blocks.sort((a, b) => a.index > b.index ? 1 : -1);
      const curBlock = blocks.at(planState.blockIdx);
      if (!curBlock?.weeks) {
        setLoading(false);
        return;
      }

      const weeks = curBlock.weeks.sort((a, b) => a.index > b.index ? 1 : -1);
      const curWeek = weeks.at(planState.weekIdx);
      if (!curWeek?.workouts) {
        setLoading(false);
        return;
      }

      try {
        const workoutPromises = curWeek.workouts.map(workout => getWorkout(workout.id));
        const workoutResponses = await Promise.all(workoutPromises);
        const allWorkouts = workoutResponses.map(res => res.data);

        const { breakdown, stats } = calculateMuscleBreakdown(allWorkouts, curWeek.workouts);
        setMuscleBreakdown(breakdown);
        setStats(stats);
      } catch (error) {
        console.error("Error fetching workouts:", error);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchAllWorkouts();
  }, [planState, reloadKey]);

  const calculateMuscleBreakdown = (workouts, weekWorkouts) => {
    const muscleData = {};
    let totalSets = 0;
    let totalExercises = 0;
    const exerciseIds = new Set();

    targetMuscles.forEach(muscle => {
      muscleData[muscle.value] = { name: muscle.name, sets: 0, exercises: 0 };
    });

    workouts.forEach(workout => {
      workout.programExercises?.forEach(exercise => {
        if (exercise.targetMuscle && exercise.intervals) {
          const muscle = exercise.targetMuscle;
          const setsCount = exercise.intervals.length;

          if (muscleData[muscle]) {
            muscleData[muscle].sets += setsCount;
            muscleData[muscle].exercises += 1;
          }

          totalSets += setsCount;

          if (exercise.exerciseId && !exerciseIds.has(exercise.exerciseId)) {
            exerciseIds.add(exercise.exerciseId);
            totalExercises++;
          } else if (!exercise.exerciseId) {
            totalExercises++;
          }
        }
      });
    });

    const completedWorkouts = weekWorkouts.filter(w => w.stateType === "COMPLETED").length;

    const groupedBreakdown = {};
    Object.entries(muscleData).forEach(([muscleKey, muscleInfo]) => {
      if (muscleInfo.sets > 0 && muscleGroupings[muscleKey]) {
        const grouping = muscleGroupings[muscleKey];
        const groupKey = grouping.group;

        if (!groupedBreakdown[groupKey]) {
          groupedBreakdown[groupKey] = {
            name: grouping.name,
            sets: 0,
            exercises: 0,
            order: grouping.order
          };
        }

        groupedBreakdown[groupKey].sets += muscleInfo.sets;
        groupedBreakdown[groupKey].exercises += muscleInfo.exercises;
      }
    });

    return {
      breakdown: groupedBreakdown,
      stats: {
        totalSets,
        totalExercises,
        completedWorkouts,
        totalWorkouts: weekWorkouts.length
      }
    };
  };

  if (loading) return null;

  const allCompleted = stats.completedWorkouts === stats.totalWorkouts && stats.totalWorkouts > 0;

  return (
    <Box sx={{
      backgroundColor: "#FFFFFF",
      borderBottom: "1px solid #E2E8F0"
    }}>
      {/* Quick Stats Row */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-around",
        padding: "12px 16px"
      }}>
        <StatItem
          icon={<FitnessCenter sx={{ fontSize: "16px" }} />}
          label="סטים"
          value={stats.totalSets}
        />
        <Box sx={{ width: "1px", backgroundColor: "#E2E8F0" }} />
        <StatItem
          icon={<Assessment sx={{ fontSize: "16px" }} />}
          label="תרגילים"
          value={stats.totalExercises}
        />
        <Box sx={{ width: "1px", backgroundColor: "#E2E8F0" }} />
        <StatItem
          icon={<CheckCircle sx={{ fontSize: "16px" }} />}
          label="הושלמו"
          value={`${stats.completedWorkouts}/${stats.totalWorkouts}`}
          highlight={allCompleted}
        />
      </Box>

      {/* Expandable Muscle Breakdown */}
      {Object.keys(muscleBreakdown).length > 0 && (
        <>
          <Box
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px",
              backgroundColor: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
              cursor: "pointer"
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "#64748B", fontWeight: 500 }}>
              {expanded ? "הסתר פירוט שרירים" : "הצג פירוט שרירים"}
            </Typography>
            {expanded ? (
              <ExpandLess sx={{ fontSize: "18px", color: "#64748B" }} />
            ) : (
              <ExpandMore sx={{ fontSize: "18px", color: "#64748B" }} />
            )}
          </Box>

          <Collapse in={expanded}>
            <Box sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              padding: "12px 16px",
              justifyContent: "center",
              backgroundColor: "#FAFBFC"
            }}>
              {Object.entries(muscleBreakdown)
                .sort(([, a], [, b]) => b.sets - a.sets)
                .map(([key, data]) => (
                  <Chip
                    key={key}
                    label={`${data.name}: ${data.sets}`}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      backgroundColor: "#F1F5F9",
                      border: "1px solid #E2E8F0"
                    }}
                  />
                ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

const StatItem = ({ icon, label, value, highlight }) => (
  <Box sx={{ textAlign: "center", flex: 1 }}>
    <Box sx={{
      color: highlight ? "#22C55E" : "#64748B",
      marginBottom: "4px",
      display: "flex",
      justifyContent: "center"
    }}>
      {icon}
    </Box>
    <Typography sx={{
      fontSize: "18px",
      fontWeight: 700,
      color: highlight ? "#22C55E" : "#1E293B",
      lineHeight: 1.2
    }}>
      {value}
    </Typography>
    <Typography sx={{
      fontSize: "10px",
      color: "#94A3B8",
      lineHeight: 1.2
    }}>
      {label}
    </Typography>
  </Box>
);

export default MobileWeekSummary;
